import type { Subscription } from '@/types'

/**
 * A subscription's billing frequency in weeks: 1 weekly, 2 fortnightly, and so
 * on (CRM tsg_billingfrequency). Missing counts as weekly.
 *
 * Every line must match its subscription's frequency, and each frequency is
 * its own set of products in CRM (TSG, DF-376), so prices are always loaded
 * for the frequency of the subscription a product is going on.
 */
export const billingFrequencyOf = (subscription?: Subscription | null): number =>
  Number(subscription?.billing_frequency) || 1

/**
 * The distinct frequencies across the customer's live subscriptions, as a
 * stable string ("1,2"), so it can key effects without re-running every time
 * the subscriptions are refetched. Weekly when there are none yet.
 */
export const billingFrequencyKey = (subscriptions: Subscription[]): string => {
  const set = new Set(subscriptions.filter(s => s.statecode !== 1).map(billingFrequencyOf))
  return set.size > 0 ? Array.from(set).sort((a, b) => a - b).join(',') : '1'
}

export const frequenciesFromKey = (key: string): number[] => key.split(',').map(Number)

/** A subscription a new product can go on, one per billing frequency. */
export interface AddSchedule {
  subscriptionId: string
  frequency: number
  /** The subscription's start date, which its delivery weeks count from. */
  startDate?: string | null
}

/**
 * The schedules a new product can be added to (DF-380): one subscription per
 * frequency, the first of each in the backend's fixed order. Cancelled
 * subscriptions and CRM quotations (no ChargeBee id) are never offered.
 */
export const addSchedules = (subscriptions: Subscription[]): AddSchedule[] => {
  const byFrequency = new Map<number, AddSchedule>()
  subscriptions.forEach(s => {
    if (s.statecode === 1 || !s.chargebee_id) return
    const frequency = billingFrequencyOf(s)
    if (!byFrequency.has(frequency)) byFrequency.set(frequency, { subscriptionId: s.id, frequency, startDate: s.start_date })
  })
  return Array.from(byFrequency.values()).sort((a, b) => a.frequency - b.frequency)
}

/** How a frequency reads to a customer: "Every week", "Every 2 weeks". */
export const howOftenLabel = (frequency: number): string =>
  frequency <= 1 ? 'Every week' : `Every ${frequency} weeks`

/**
 * The customer's main subscription: the first live one that is a real
 * subscription (DF-381). CRM quotations sit in the list too, with no
 * ChargeBee id, and nothing can be added to or paused on them, so they are
 * skipped. Falls back to the first live one only if that is all there is.
 */
export const mainSubscription = (subscriptions: Subscription[]): Subscription | null => {
  const live = subscriptions.filter(s => s.statecode !== 1)
  return live.find(s => !!s.chargebee_id) ?? live[0] ?? null
}

/**
 * Each subscription's billing frequency, keyed by its CRM id (external_id),
 * which is what a line carries as subscription_external_id. Lets a product
 * show the frequency of the subscription it is on (DF-392).
 */
export const frequencyBySubscription = (subscriptions: Subscription[]): Map<string, number> =>
  new Map(subscriptions.map(s => [s.external_id, billingFrequencyOf(s)]))

/**
 * The customer-facing frequency tag, shown on a product in Delivery Management
 * and My Deliveries: "Weekly", "Every 2 weeks" ... (CRM tsg_billingfrequency
 * 1 = weekly ... 4 = every 4 weeks). Null when the frequency isn't known.
 */
export const frequencyTag = (code: string | number | null | undefined): string | null => {
  switch (String(code ?? '')) {
    case '1': return 'Weekly'
    case '2': return 'Every 2 weeks'
    case '3': return 'Every 3 weeks'
    case '4': return 'Every 4 weeks'
    default: return null
  }
}
